import { X, RotateCcw } from 'lucide-react';
import { WIDGET_REGISTRY, WIDGET_CATEGORIES } from './widgetRegistry';

interface Props {
  enabledIds: string[];
  allowedIds?: Set<string>;
  onToggle: (id: string) => void;
  onReset: () => void;
  onClose: () => void;
}

export function WidgetSelector({ enabledIds, allowedIds, onToggle, onReset, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-sm overflow-y-auto bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Customize Widgets</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Widget list grouped by category */}
        <div className="space-y-4 px-5 py-4">
          {WIDGET_CATEGORIES.map((cat) => {
            const widgets = WIDGET_REGISTRY.filter((w) => w.category === cat.id && (!allowedIds || allowedIds.has(w.id)));
            if (widgets.length === 0) return null;
            const allEnabled = widgets.every((w) => enabledIds.includes(w.id));

            return (
              <div key={cat.id}>
                {/* Category header */}
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${cat.color}`} />
                    <span className="text-sm font-semibold text-gray-700">{cat.label}</span>
                    <span className="text-xs text-gray-400">({widgets.length})</span>
                  </div>
                  <button
                    onClick={() => {
                      // Toggle all in category
                      widgets.forEach((w) => {
                        const isOn = enabledIds.includes(w.id);
                        if (allEnabled && isOn) onToggle(w.id);
                        if (!allEnabled && !isOn) onToggle(w.id);
                      });
                    }}
                    className="text-xs font-medium text-[var(--ink-0)] hover:text-[var(--ink-0)]"
                  >
                    {allEnabled ? 'Hide All' : 'Show All'}
                  </button>
                </div>

                {/* Widget checkboxes */}
                <div className="space-y-1">
                  {widgets.map((w) => {
                    const isOn = enabledIds.includes(w.id);
                    const Icon = w.icon;
                    return (
                      <label
                        key={w.id}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
                          isOn ? 'bg-[var(--saffron-50)]' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isOn}
                          onChange={() => onToggle(w.id)}
                          className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]"
                        />
                        <div className={`flex h-6 w-6 items-center justify-center rounded ${w.color}`}>
                          <Icon size={12} className={w.iconColor} />
                        </div>
                        <span className="text-sm text-gray-700">{w.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t border-gray-200 bg-white px-5 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={onReset}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
            >
              <RotateCcw size={14} />
              Reset to Default
            </button>
            <button
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium"
              style={{ background: 'var(--ink-0)', color: 'var(--ivory-0)', border: '1px solid var(--ink-0)' }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
